import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { getAdminUsers } from 'ServerAPI'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import Select from 'components/base/Select'
import MenuItem from 'components/base/MenuItem'
import { AppRoutes } from '~/AppRoutes'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { formatUnixTime } from '~/components/admin/formatUnixTime'
import { getTruncatedValue } from '~/components/admin/ValuePopup'
import { usePageSize } from '~/components/admin/usePageSize'
import './RecentUsers.scss'

const searchByOptions = [
  'userId',
  'name',
  'eMail',
  'apiKey',
  'secreteAPIKey',
]

const hasValue = value => value !== null && value !== undefined && value !== ''

const getQueryState = locationSearch => {
  const queryParams = new URLSearchParams(locationSearch)
  const searchByFromQuery = queryParams.get('searchBy') || queryParams.get('sortBy')
  return {
    search: queryParams.get('search') || '',
    searchBy: searchByOptions.includes(searchByFromQuery) ? searchByFromQuery : 'name',
  }
}

const getSearchUrl = (searchBy, search) => {
  const queryParams = new URLSearchParams()
  queryParams.set('sortBy', searchBy)
  queryParams.set('search', String(search))
  return `${AppRoutes.AdminRecentUsers}?${queryParams.toString()}`
}

const getKeyValue = (value, searchBy) => {
  if (!hasValue(value)) return value
  return (
    <Link
      className="admin-panel__link"
      title={String(value)}
      to={getSearchUrl(searchBy, value)}
    >
      {getTruncatedValue(value)}
    </Link>
  )
}

export const RecentUsers = () => {
  const { pageSize, tableRef, isReady } = usePageSize()
  const location = useLocation()
  const initialQueryState = getQueryState(location.search)
  const [ searchText, setSearchText ] = useState(initialQueryState.search)
  const [ search, setSearch ] = useState(initialQueryState.search)
  const [ searchBy, setSearchBy ] = useState(initialQueryState.searchBy)
  const [ start, setStart ] = useState(0)
  const [ users, setUsers ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ loading, setLoading ] = useState(false)

  useEffect(() => {
    const nextQueryState = getQueryState(location.search)
    setStart(0)
    setSearchText(nextQueryState.search)
    setSearch(nextQueryState.search)
    setSearchBy(nextQueryState.searchBy)
  }, [ location.search ])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminUsers({ start_number: start, count: pageSize, search, searchBy })
      setUsers(data.users || [])
      setTotalCount(data.totalCount || 0)
    } catch (err) {
      setUsers([])
      setTotalCount(0)
      // eslint-disable-next-line no-console
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [ start, search, searchBy, pageSize ])

  useEffect(() => {
    if (!isReady) return
    load()
  }, [ load, isReady ])

  const onSearch = () => {
    setStart(0)
    setSearch(searchText)
  }

  const onPage = diff => {
    const nextStart = start + diff * pageSize
    if (nextStart < 0 || nextStart >= totalCount) return
    setStart(nextStart)
  }

  const currentPage = totalCount ? Math.floor(start / pageSize) + 1 : 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="admin-list">
      <div className="admin-list__search">
        <span className="admin-list__search__label">Search by</span>
        <Select
          className="admin-list__search__select admin-list__search__select--users"
          dropdownClassName="admin-list__search__dropdown--users"
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value)
            setStart(0)
          }}
        >
          {searchByOptions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
        <Input
          id="admin-users-search"
          className="admin-list__search__input"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onEnterKeyDown={onSearch}
        />
        <Button className="admin-list__search__button" primary onClick={onSearch}>
          Search
        </Button>
      </div>

      <div className="admin-list__table" ref={tableRef}>
        <Table aria-label="recent users">
          <TableHead>
            <TableRow>
              <TableCell>userId</TableCell>
              <TableCell>apiKey</TableCell>
              <TableCell>secreteAPIKey</TableCell>
              <TableCell>name</TableCell>
              <TableCell>eMail</TableCell>
              <TableCell>activeTime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow key={user.userId || index}>
                <TableCell>
                  <Link
                    className="admin-panel__link"
                    to={`${AppRoutes.AdminRecentUsers}/${user.userId}`}
                  >
                    {user.userId}
                  </Link>
                </TableCell>
                <TableCell>{getKeyValue(user.apiKey, 'apiKey')}</TableCell>
                <TableCell>{getKeyValue(user.secreteAPIKey, 'secreteAPIKey')}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.eMail}</TableCell>
                <TableCell>{formatUnixTime(user.activeTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="admin-list__footer">
        <If condition={loading}>
          <CircularProgress endless percents={80} transparent size={24} />
        </If>
        <div className="admin-list__footer__nav">
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(-1)}
            disabled={start === 0}
          >
            Prev
          </Button>
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(1)}
            disabled={totalCount === 0 || start + pageSize >= totalCount}
          >
            Next
          </Button>
        </div>
        <span className="admin-list__footer__info">
          {'page '}
          {String(currentPage)}
          {' of '}
          {String(totalPages)}
          {', totalCount: '}
          {String(totalCount)}
        </span>
      </div>
    </div>
  )
}
